import dayjs from 'dayjs';
import { toast } from 'sonner';
import { create } from 'zustand';
import { formatNumberDecimaWithoutRoundoff } from '../utility/helpers/commonFunction';
import useGlobalStore from './useGlobalStore';

const initialState = {
    showLoader: false,
    customerDetail: '',
    recordPaymentParams: {
        pagination: true,
        page: 1,
        limit: 500000,
        status: '',
        sortType: 'DSC',
        sortBy: 'createdAt',
        fromDate: '',
        toDate: '',
        searchText: '',
        customer: [],
        company: [],
        status: ['remaining'],
    },
    recordPaymentList: [],
    modifiedRecordPaymentList: [],
    selectedRows: [],
    recordPaymentPagaintion: {},
    // NEW: Store for maintaining selected records that may not be visible
    selectedRecordsCache: new Map(), // Map<invoiceId, modifiedInvoiceData>
};

const recordPaymentStore = (set, get) => ({
    ...initialState,

    setCustomerDetail: (data) => {
        set({ customerDetail: data });
    },

    setShowLoader: (data) => {
        set({ showLoader: data });
    },

    setRecordPaymentParams: (data) => {
        set({ recordPaymentParams: data });
    },

    setRecordPaymentList: (data) => {
        const { selectedRows, selectedRecordsCache } = get();

        // Deep clone the new data
        const clonedData = data.map((invoice) => {
            // Check if this invoice is selected and has cached modifications
            if (selectedRows.includes(invoice._id) && selectedRecordsCache.has(invoice._id)) {
                // Use the cached modified version instead of the fresh API data
                return selectedRecordsCache.get(invoice._id);
            }

            // For non-selected or new invoices, process normally
            return {
                ...invoice,
                Total: formatNumberDecimaWithoutRoundoff(invoice.Total),
                remainingPayment: formatNumberDecimaWithoutRoundoff(invoice.remainingPayment),
                payments: invoice.payments?.length
                    ? invoice.payments.map((payment) => ({
                          transactionType: payment.transactionType || 'payment',
                          paymentMethod: payment.paymentMethod || 'credit',
                          amount: formatNumberDecimaWithoutRoundoff(payment.amount || 0).toString(),
                          paymentReference: payment.paymentReference || '',
                          paymentDate: payment.paymentDate || null,
                          _id: payment._id,
                          isExisting: true,
                      }))
                    : [{ transactionType: 'payment', paymentMethod: '', amount: '', paymentReference: '', isExisting: false }],
            };
        });

        set({
            recordPaymentList: data, // Original data
            modifiedRecordPaymentList: clonedData, // Modified data with preserved changes
        });
    },

    setSelectedRows: (data) => {
        const { selectedRows: currentSelectedRows, modifiedRecordPaymentList, selectedRecordsCache } = get();
        const newSelectedRows = Array.isArray(data) ? data : [];

        // Find newly deselected rows
        const deselectedRows = currentSelectedRows.filter((id) => !newSelectedRows.includes(id));

        // Remove deselected rows from cache
        const newCache = new Map(selectedRecordsCache);
        deselectedRows.forEach((id) => {
            newCache.delete(id);
        });

        // Add/update cache for currently selected rows that are visible
        modifiedRecordPaymentList.forEach((invoice) => {
            if (newSelectedRows.includes(invoice._id)) {
                newCache.set(invoice._id, { ...invoice });
            }
        });

        set({
            selectedRows: newSelectedRows,
            selectedRecordsCache: newCache,
        });

        // Update global dirty flag based on selection
        const { isDirty, markSaved } = useGlobalStore.getState();
        if (isDirty && newSelectedRows.length > 0) {
        } else if (newSelectedRows.length > 0) {
            markSaved(true);
        } else {
            markSaved(false);
        }
    },

    setRecordPaymentPagaintion: (data) => {
        set({ recordPaymentPagaintion: data });
    },

    // Updated methods to also update the cache
    updatePaymentInModifiedList: (invoiceId, paymentIndex, updatedFields) => {
        set((state) => {
            const newModifiedList = state.modifiedRecordPaymentList.map((invoice) =>
                invoice._id === invoiceId
                    ? {
                          ...invoice,
                          payments: invoice.payments.map((payment, index) => (index === paymentIndex ? { ...payment, ...updatedFields } : payment)),
                      }
                    : invoice
            );

            // Update cache for selected rows
            const newCache = new Map(state.selectedRecordsCache);
            const updatedInvoice = newModifiedList.find((inv) => inv._id === invoiceId);
            if (updatedInvoice && state.selectedRows.includes(invoiceId)) {
                newCache.set(invoiceId, { ...updatedInvoice });
            }

            return {
                modifiedRecordPaymentList: newModifiedList,
                selectedRecordsCache: newCache,
            };
        });
    },

    addPaymentToModifiedList: (invoiceId) => {
        set((state) => {
            const newModifiedList = state.modifiedRecordPaymentList.map((invoice) =>
                invoice._id === invoiceId
                    ? {
                          ...invoice,
                          payments: [
                              ...invoice.payments,
                              {
                                  paymentMethod: 'credit',
                                  amount: '',
                                  paymentReference: '',
                                  isExisting: false,
                              },
                          ],
                      }
                    : invoice
            );

            // Update cache
            const newCache = new Map(state.selectedRecordsCache);
            const updatedInvoice = newModifiedList.find((inv) => inv._id === invoiceId);
            if (updatedInvoice && state.selectedRows.includes(invoiceId)) {
                newCache.set(invoiceId, { ...updatedInvoice });
            }

            return {
                modifiedRecordPaymentList: newModifiedList,
                selectedRecordsCache: newCache,
            };
        });
    },

    deletePaymentFromModifiedList: (invoiceId, paymentIndex) => {
        set((state) => {
            const newModifiedList = state.modifiedRecordPaymentList.map((invoice) =>
                invoice._id === invoiceId
                    ? {
                          ...invoice,
                          payments: invoice.payments.filter((_, index) => index !== paymentIndex),
                      }
                    : invoice
            );

            // Update cache
            const newCache = new Map(state.selectedRecordsCache);
            const updatedInvoice = newModifiedList.find((inv) => inv._id === invoiceId);
            if (updatedInvoice && state.selectedRows.includes(invoiceId)) {
                newCache.set(invoiceId, { ...updatedInvoice });
            }

            return {
                modifiedRecordPaymentList: newModifiedList,
                selectedRecordsCache: newCache,
            };
        });
    },

    // Updated discard changes to clear cache
    discardChanges: () => {
        const { recordPaymentList } = get();

        // Re-clone the original data
        const clonedData = recordPaymentList.map((invoice) => ({
            ...invoice,
            Total: formatNumberDecimaWithoutRoundoff(invoice.Total),
            remainingPayment: formatNumberDecimaWithoutRoundoff(invoice.remainingPayment),
            payments: invoice.payments?.length
                ? invoice.payments.map((payment) => ({
                      transactionType: payment.transactionType || 'payment',
                      paymentMethod: payment.paymentMethod || 'credit',
                      amount: formatNumberDecimaWithoutRoundoff(payment.amount || 0).toString(),
                      paymentReference: payment.paymentReference || '',
                      paymentDate: payment.paymentDate || null,
                      _id: payment._id,
                      isExisting: true,
                  }))
                : [{ transactionType: 'payment', paymentMethod: '', amount: '', paymentReference: '', isExisting: false }],
        }));

        set({
            modifiedRecordPaymentList: clonedData,
            selectedRecordsCache: new Map(), // Clear cache
        });

        // Reset dirty state
        const { markSaved } = useGlobalStore.getState();
        markSaved(false);
    },

    // Updated getSavePayload to use cache for selected records not in current view
    getSavePayload: () => {
        const { modifiedRecordPaymentList, selectedRows, selectedRecordsCache } = get();
        let isValid = true;

        // Create a combined list of all selected records (visible + cached)
        const allSelectedRecords = [];

        // Add visible selected records
        modifiedRecordPaymentList.forEach((invoice) => {
            if (selectedRows.includes(invoice._id)) {
                allSelectedRecords.push(invoice);
            }
        });

        // Add cached selected records that are not in current view
        selectedRows.forEach((selectedId) => {
            const isInCurrentView = modifiedRecordPaymentList.some((inv) => inv._id === selectedId);
            if (!isInCurrentView && selectedRecordsCache.has(selectedId)) {
                allSelectedRecords.push(selectedRecordsCache.get(selectedId));
            }
        });

        const payload = allSelectedRecords.map((invoice) => {
            const validPayments = [];

            for (const payment of invoice.payments) {
                const amount = parseFloat(payment.amount || 0);
                const method = payment.paymentMethod?.trim() || '';
                const invoiceNo = invoice.invoiceNumber || invoice.invoiceNo || invoice._id;
                const paymentDate = payment.paymentDate ? dayjs(payment.paymentDate).format('YYYY-MM-DD') : null;

                // Validation logic
                if (method && (amount === 0 || isNaN(amount))) {
                    console.log('Amount is required for invoice #' + invoiceNo);
                    toast.error(`Amount is required for invoice #${invoiceNo}`);
                    isValid = false;
                    break;
                }

                if (amount > 0 && !method) {
                    toast.error(`Payment method is required for invoice #${invoiceNo}`);
                    isValid = false;
                    break;
                }

                if (!method && (amount === 0 || isNaN(amount))) {
                    toast.error(`Payment method is required for invoice #${invoiceNo}`);
                    isValid = false;
                    break;
                }

                // Create valid payment
                const { isExisting, ...rest } = payment;
                let finalPayment = { ...rest };

                if (paymentDate) {
                    finalPayment.paymentDate = dayjs(paymentDate).format('YYYY-MM-DD');
                } else {
                    delete finalPayment.paymentDate;
                }

                if (!rest.transactionType) {
                    finalPayment.transactionType = 'payment';
                }

                validPayments.push(finalPayment);
            }

            return {
                invoiceId: invoice._id,
                payments: validPayments,
            };
        });

        return { isValid, payload };
    },

    // Updated getTotalAdditionalPayment to include cached records
    getTotalAdditionalPayment: () => {
        const { recordPaymentList, modifiedRecordPaymentList, selectedRows, selectedRecordsCache } = get();

        // Map original payments by invoiceId for easy lookup
        const originalMap = recordPaymentList.reduce((acc, invoice) => {
            acc[invoice._id] = (invoice?.payments || []).map((p) => parseFloat(p.amount?.$numberDecimal || p.amount || 0));
            return acc;
        }, {});

        let difference = 0;

        // Process visible selected records
        modifiedRecordPaymentList.forEach((invoice) => {
            if (!selectedRows.includes(invoice._id)) return;

            const originalPayments = originalMap[invoice._id] || [];
            const modifiedPayments = (invoice?.payments || []).map((p) => parseFloat(p.amount || 0));

            const originalTotal = originalPayments.reduce((sum, v) => sum + v, 0);
            const modifiedTotal = modifiedPayments.reduce((sum, v) => sum + v, 0);

            difference += modifiedTotal - originalTotal;
        });

        // Process cached selected records not in current view
        selectedRows.forEach((selectedId) => {
            const isInCurrentView = modifiedRecordPaymentList.some((inv) => inv._id === selectedId);
            if (!isInCurrentView && selectedRecordsCache.has(selectedId)) {
                const cachedInvoice = selectedRecordsCache.get(selectedId);
                const originalPayments = originalMap[selectedId] || [];
                const modifiedPayments = (cachedInvoice?.payments || []).map((p) => parseFloat(p.amount || 0));

                const originalTotal = originalPayments.reduce((sum, v) => sum + v, 0);
                const modifiedTotal = modifiedPayments.reduce((sum, v) => sum + v, 0);

                difference += modifiedTotal - originalTotal;
            }
        });

        return difference;
    },

    resetRecordPayment: () => {
        set({ ...initialState, selectedRecordsCache: new Map() });
        const { markSaved } = useGlobalStore.getState();
        markSaved(false);
    },

    // Helper method to get all selected records (visible + cached) for UI purposes
    getAllSelectedRecords: () => {
        const { modifiedRecordPaymentList, selectedRows, selectedRecordsCache } = get();
        const allRecords = [];

        // Add visible records
        modifiedRecordPaymentList.forEach((invoice) => {
            if (selectedRows.includes(invoice._id)) {
                allRecords.push(invoice);
            }
        });

        // Add cached records not in current view
        selectedRows.forEach((selectedId) => {
            const isInCurrentView = modifiedRecordPaymentList.some((inv) => inv._id === selectedId);
            if (!isInCurrentView && selectedRecordsCache.has(selectedId)) {
                allRecords.push(selectedRecordsCache.get(selectedId));
            }
        });

        return allRecords;
    },
});

const useRecordPaymentStore = create(recordPaymentStore);

export default useRecordPaymentStore;
